import { SearchOutlined } from "@ant-design/icons";
import { Checkbox, Input } from "antd";
import { useMemo, useState } from "react";
import Button from "../../../Components/Button";
import Modal from "../../../Components/Modal/Modal";
import { IPermissionTreeNode } from "../../../Interfaces/roleAndPermissionsInterfaces";

interface IProps {
  options: IPermissionTreeNode[];
  value: number[];
  loading: boolean;
  onChange: (value: number[]) => void;
}

const normalize = (value: string | null | undefined) =>
  (value || "").trim().toLocaleLowerCase("fa");

const nodeMatches = (node: IPermissionTreeNode, search: string) => {
  const normalizedSearch = normalize(search);
  if (!normalizedSearch) return true;

  return [node.title, node.policy, node.description, node.clientAddress].some(
    (value) => normalize(value).includes(normalizedSearch)
  );
};

const filterTree = (
  nodes: IPermissionTreeNode[],
  search: string
): IPermissionTreeNode[] => {
  if (!search.trim()) return nodes;

  return nodes.flatMap((node) => {
    const children = filterTree(node.children || [], search);
    if (nodeMatches(node, search)) return [node];
    if (children.length === 0) return [];
    return [{ ...node, children }];
  });
};

export const collectPermissionIds = (node: IPermissionTreeNode): number[] => {
  const ownId = node.selectable && node.permissionId ? [node.permissionId] : [];
  return [
    ...ownId,
    ...(node.children || []).flatMap(collectPermissionIds),
  ];
};

const PermissionCheckbox = ({
  node,
  selectedIds,
  onToggle,
}: {
  node: IPermissionTreeNode;
  selectedIds: number[];
  onToggle: (node: IPermissionTreeNode) => void;
}) => {
  const permissionIds = collectPermissionIds(node);
  const selectedCount = permissionIds.filter((id) =>
    selectedIds.includes(id)
  ).length;
  const checked = permissionIds.length > 0 && selectedCount === permissionIds.length;
  const indeterminate = selectedCount > 0 && selectedCount < permissionIds.length;

  return (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={permissionIds.length === 0}
      onChange={() => onToggle(node)}
    />
  );
};

const PermissionPickerModal = ({
  options,
  value,
  loading,
  onChange,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<number[]>(value || []);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(
    () => filterTree(options, search),
    [options, search]
  );

  const openModal = () => {
    setDraftValue(value || []);
    setSearch("");
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setSearch("");
  };

  const toggleNode = (node: IPermissionTreeNode) => {
    const nodeIds = collectPermissionIds(node);
    const allSelected = nodeIds.every((id) => draftValue.includes(id));

    if (allSelected) {
      setDraftValue(draftValue.filter((id) => !nodeIds.includes(id)));
      return;
    }

    setDraftValue(Array.from(new Set([...draftValue, ...nodeIds])));
  };

  const confirm = () => {
    onChange(draftValue);
    closeModal();
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <span className="text-xs text-neutral-600">دسترسی‌ها</span>
      <Button
        variant="outlined"
        text={
          loading
            ? "در حال دریافت دسترسی‌ها..."
            : value?.length
              ? `${value.length} دسترسی انتخاب شده`
              : "انتخاب دسترسی‌ها"
        }
        onClick={openModal}
        disabled={loading}
        className="!justify-between"
      />

      {isOpen ? (
        <Modal
          onClose={closeModal}
          closeOnBackdropClick
          modalBoxClassName="!w-[1100px] !max-w-[96vw]"
          UI={
            <div className="flex max-h-[82vh] flex-col gap-4" dir="rtl">
              <div className="flex flex-col gap-3 border-b pb-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-base font-bold">انتخاب سطح دسترسی</h2>
                  <p className="mt-1 text-xs text-neutral-500">
                    دسترسی‌ها بر اساس منوی اصلی، صفحهٔ فرانت و عملیات‌های هر صفحه دسته‌بندی شده‌اند.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-600">
                  <span>{draftValue.length} مورد انتخاب شده</span>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-red-600 hover:bg-red-50"
                    onClick={() => setDraftValue([])}
                  >
                    پاک‌کردن همه
                  </button>
                </div>
              </div>

              <Input
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="جستجو در نام منو، صفحه، Policy یا عملیات..."
                prefix={<SearchOutlined className="text-neutral-400" />}
              />

              <div className="flex-1 space-y-3 overflow-y-auto pl-1">
                {filteredOptions.map((menu) => (
                  <section key={menu.key} className="overflow-hidden rounded-lg border border-neutral-200">
                    <div className="flex items-center gap-2 border-b bg-sky-50 px-3 py-2">
                      <PermissionCheckbox
                        node={menu}
                        selectedIds={draftValue}
                        onToggle={toggleNode}
                      />
                      <h3 className="font-semibold text-sky-900">{menu.title}</h3>
                      <span className="text-[11px] text-neutral-500">
                        ({collectPermissionIds(menu).length} دسترسی)
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3 p-3 lg:grid-cols-2">
                      {menu.children.map((page) => (
                        <div key={page.key} className="rounded-md border border-neutral-200 bg-white">
                          <div className="flex items-start gap-2 border-b bg-neutral-50 px-3 py-2">
                            <PermissionCheckbox
                              node={page}
                              selectedIds={draftValue}
                              onToggle={toggleNode}
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-neutral-800">
                                {page.title}
                                {page.policy ? (
                                  <span className="mr-1 font-mono text-[11px] text-sky-700">
                                    ({page.policy})
                                  </span>
                                ) : null}
                              </div>
                              {page.description && page.description !== page.title ? (
                                <div className="mt-0.5 text-[11px] text-neutral-500">
                                  {page.description}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {page.children.length ? (
                            <div className="grid grid-cols-1 gap-1 p-2 sm:grid-cols-2">
                              {page.children.map((permission) => (
                                <div
                                  key={permission.key}
                                  className="flex items-start gap-2 rounded px-2 py-1.5 hover:bg-sky-50"
                                >
                                  <PermissionCheckbox
                                    node={permission}
                                    selectedIds={draftValue}
                                    onToggle={toggleNode}
                                  />
                                  <span className="min-w-0">
                                    <span className="block text-xs text-neutral-800">
                                      {permission.title}
                                    </span>
                                    {permission.policy ? (
                                      <span className="block truncate font-mono text-[10px] text-neutral-400">
                                        {permission.policy}
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}

                {filteredOptions.length === 0 ? (
                  <div className="py-12 text-center text-sm text-neutral-500">
                    نتیجه‌ای پیدا نشد.
                  </div>
                ) : null}
              </div>

              <div className="flex justify-end gap-2 border-t pt-3">
                <Button
                  variant="outlined"
                  text="انصراف"
                  onClick={closeModal}
                  className="!w-24"
                />
                <Button
                  variant="contained"
                  text={`تأیید انتخاب (${draftValue.length})`}
                  onClick={confirm}
                  className="!w-40"
                />
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
};

export default PermissionPickerModal;
